import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { clustersAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCluster } from '../context/ClusterContext';
import GroupAccessPanel from '../components/GroupAccessPanel';
import NamespaceTeamsModal from '../components/NamespaceTeamsModal';

/**
 * Namespace access for non-admin users, via Rancher Projects.
 *
 * Nothing here decides permissions — Rancher does, and Steve enforces it on every read.
 * This screen only drives Rancher's own API, because the browser cannot call it directly.
 *
 * Three things must line up or a namespace stays invisible to a non-admin:
 *   1. a Project exists          2. the namespace is assigned to it
 *   3. the user is bound to it
 *
 * Granting a user creates their Rancher identity and token as a side effect, so there is no
 * step for the user to complete — they never see a Rancher prompt.
 *
 * The screen is built around that: the unassigned count is the headline, because on dev45
 * 160 of 172 namespaces belonged to no project at all and were invisible to everyone
 * except admins.
 */

const S = {
  page: { padding: '32px', fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ink-2)' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 16, flexWrap: 'wrap' },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginTop: 4, maxWidth: 640, lineHeight: 1.5 },
  statRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 },
  stat: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 18px', minWidth: 120 },
  statNum: { fontSize: 22, fontWeight: 700, lineHeight: 1.1 },
  statLabel: { fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 4 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 },
  cardTitle: { fontSize: 15, fontWeight: 700, margin: 0, marginBottom: 4 },
  cardHint: { fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 },
  toolbar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 },
  input: { padding: '7px 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--ink-2)', fontSize: 13, minWidth: 180 },
  select: { padding: '7px 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--ink-2)', fontSize: 13 },
  btn: { padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--ink-2)', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  btnPrimary: { padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  btnDanger: { padding: '4px 10px', borderRadius: 6, border: '1px solid var(--color-danger-500)', background: 'transparent', color: 'var(--color-danger-500)', fontSize: 12, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--muted)', position: 'sticky', top: 0, background: 'var(--surface)' },
  td: { padding: '7px 10px', borderBottom: '1px solid var(--surface-2)' },
  scroll: { maxHeight: 460, overflowY: 'auto', overflowX: 'auto' },
  pill: (bg, fg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' }),
  empty: { padding: '36px 16px', textAlign: 'center', color: 'var(--muted-2)', fontSize: 14 },
  banner: (kind) => ({
    padding: '12px 16px', borderRadius: 10, fontSize: 13, marginBottom: 16, lineHeight: 1.5,
    background: kind === 'error' ? 'rgba(220,38,38,0.08)' : kind === 'warn' ? 'rgba(217,119,6,0.10)' : 'rgba(16,185,129,0.10)',
    border: `1px solid ${kind === 'error' ? 'var(--color-danger-500)' : kind === 'warn' ? 'var(--color-warning-500)' : 'var(--color-success-500)'}`,
    color: 'var(--ink-2)',
  }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: 'var(--surface)', borderRadius: 16, padding: 28, width: 480, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, marginTop: 14 },
};

const PILL = {
  assigned: S.pill('rgba(16,185,129,0.15)', 'var(--color-success-500)'),
  unassigned: S.pill('rgba(148,163,184,0.18)', 'var(--muted)'),
  stale: S.pill('rgba(217,119,6,0.18)', 'var(--color-warning-500)'),
};

export default function NamespaceAccess() {
  const { isSuperAdmin, isAdmin } = useAuth();
  const canManage = isSuperAdmin || isAdmin;
  // The app-wide "Active Cluster" selector (sidebar) — this page used to ignore it
  // entirely and silently default to "the first Rancher cluster in the list",
  // which could be a completely different cluster than what's shown as active
  // everywhere else in the app.
  const { selectedCluster: globalCluster } = useCluster();

  const [clusters, setClusters] = useState([]);
  // clusterId is DERIVED purely from the sidebar's Active Cluster (globalCluster)
  // below — there is deliberately no page-local picker or fallback anymore. Two
  // places to pick a cluster (this page's own dropdown + the sidebar) was exactly
  // what let them drift out of sync and show a different cluster than Active.
  const [clusterId, setClusterId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [namespaces, setNamespaces] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bindings, setBindings] = useState([]);
  const [users, setUsers] = useState([]);
  const [health, setHealth] = useState({});          // userId -> {healthy,...}

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);              // {kind, text}

  const [filter, setFilter] = useState('');
  const [showOnly, setShowOnly] = useState('all');   // all | unassigned | stale | assigned
  const [selected, setSelected] = useState(new Set());
  const [bulkProject, setBulkProject] = useState('');
  const [activeProject, setActiveProject] = useState('');
  // Staged per-namespace target project, keyed by namespace name — lets the
  // user distribute different namespaces across different projects (stage
  // a subset at a time) before committing everything in one API call.
  const [pending, setPending] = useState({});

  const [newProject, setNewProject] = useState(null);   // {name, description} while modal open

  // Namespace whose "Teams…" dialog is open (null when closed).
  const [teamsFor, setTeamsFor] = useState(null);

  // Rancher clusters only — Projects are a Rancher concept, so the rest cannot appear here.
  const rancherClusters = useMemo(
    () => clusters.filter(c => c.connection_type === 'rancher'), [clusters]);

  useEffect(() => {
    clustersAPI.list()
      .then(r => setClusters(r.data || []))
      .catch(() => setMsg({ kind: 'error', text: 'Could not load clusters.' }));
  }, []);

  // clusterId always mirrors the sidebar's Active Cluster — set on initial load,
  // and re-derived any time the user switches it from the sidebar. null when the
  // Active Cluster is a type this feature doesn't support (handled below in the
  // render: an explanatory message, not a fallback to some other cluster).
  useEffect(() => {
    if (globalCluster?.connection_type === 'rancher' && clusters.some(c => c.id === globalCluster.id)) {
      setClusterId(globalCluster.id);
    } else {
      setClusterId(null);
    }
  }, [globalCluster?.id, globalCluster?.connection_type, clusters]);

  const load = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    setMsg(null);
    try {
      const [ns, pj] = await Promise.all([
        clustersAPI.listNamespaceProjects(clusterId),
        clustersAPI.listProjects(clusterId),
      ]);
      setNamespaces(ns.data.namespaces || []);
      setSummary(ns.data.summary || null);
      setProjects(pj.data.projects || []);
      setSelected(new Set());
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Failed to load namespaces.' });
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { load(); }, [load]);

  // Users, plus whether each one's cluster access still works. A Rancher token can be
  // deleted server-side while the SarvKube row looks fine, so this is the only way to see a
  // broken grant before that person opens a page and gets a 502.
  const loadUserHealth = useCallback(async () => {
    if (!canManage || !clusterId) return;
    try {
      const r = await usersAPI.list();
      const rows = (r.data || []).filter(u => !['superadmin', 'admin'].includes(u.rbac_role));
      setUsers(rows);
      const out = {};
      await Promise.all(rows.map(async u => {
        try {
          const h = await usersAPI.rancherTokenHealth(u.id);
          out[u.id] = (h.data.clusters || {})[String(clusterId)] || null;
        } catch { out[u.id] = null; }
      }));
      setHealth(out);
    } catch { /* the page still works without health info */ }
  }, [canManage, clusterId]);

  useEffect(() => { loadUserHealth(); }, [loadUserHealth]);

  const loadBindings = useCallback(async (pid) => {
    setActiveProject(pid);
    if (!pid) { setBindings([]); return; }
    try {
      const r = await clustersAPI.listProjectBindings(clusterId, pid);
      setBindings(r.data.bindings || []);
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Failed to load bindings.' });
    }
  }, [clusterId]);

  const projectName = useCallback((fullId) => {
    if (!fullId) return null;
    const short = String(fullId).split(':').pop();
    return projects.find(p => p.project_id === short)?.name || short;
  }, [projects]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return namespaces.filter(n => {
      if (q && !n.name.toLowerCase().includes(q)) return false;
      if (showOnly === 'unassigned') return !n.assigned && !n.stale;
      if (showOnly === 'stale') return n.stale;
      if (showOnly === 'assigned') return n.assigned;
      return true;
    });
  }, [namespaces, filter, showOnly]);

  const toggle = (name) => setSelected(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const toggleAllVisible = () => setSelected(prev =>
    visible.every(n => prev.has(n.name)) ? new Set() : new Set(visible.map(n => n.name)));

  // Stage the checked namespaces to bulkProject (doesn't call the API yet) —
  // clears the checkbox selection afterward so a DIFFERENT subset can be
  // checked and staged to a DIFFERENT project next, any number of times,
  // before one Apply commits everything together.
  const doStageBulk = () => {
    if (!bulkProject || selected.size === 0) return;
    setPending(prev => {
      const next = { ...prev };
      selected.forEach(ns => { next[ns] = bulkProject; });
      return next;
    });
    setSelected(new Set());
  };

  // Stage a single row's "Move to" choice, same staging mechanism as the
  // bulk toolbar above — nothing is sent to the API until Apply.
  const doStageOne = (ns, pid) => {
    setPending(prev => ({ ...prev, [ns]: pid || null }));
  };

  const doDiscardPending = () => setPending({});

  const doApplyPending = async () => {
    const entries = Object.entries(pending);
    if (entries.length === 0) return;
    const assignments = entries.map(([namespace, project_id]) => ({ namespace, project_id }));
    setBusy('apply');
    try {
      const r = await clustersAPI.bulkAssignNamespaces(clusterId, assignments);
      const { assigned, failed } = r.data.counts;
      setMsg(failed
        ? { kind: 'warn', text: `Applied ${assigned}, ${failed} failed: ` +
            r.data.failed.map(f => f.namespace).join(', ') }
        : { kind: 'ok', text: `Applied ${assigned} namespace assignment${assigned === 1 ? '' : 's'}.` });
      setPending({});
      await load();
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not apply changes.' });
    } finally { setBusy(''); }
  };

  const doCreateProject = async () => {
    const name = (newProject?.name || '').trim();
    if (!name) return;
    setBusy('project');
    try {
      const r = await clustersAPI.createProject(clusterId, {
        name, description: newProject.description || '',
      });
      setNewProject(null);
      await load();
      setBulkProject(r.data.project_id);
      setMsg({ kind: 'ok', text: `Project "${name}" created. Assign namespaces to it below.` });
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not create project.' });
    } finally { setBusy(''); }
  };

  const doBind = async (userId) => {
    if (!activeProject || !userId) return;
    setBusy('bind');
    try {
      // Send the SARVKUBE user id: the API creates their Rancher identity and token if they
      // have none, so an admin grants access to a person they already manage.
      await clustersAPI.addProjectBinding(clusterId, activeProject, {
        user_id: Number(userId), role: 'project-member',
      });
      await loadBindings(activeProject);
      await loadUserHealth();
      setMsg({ kind: 'ok', text: 'Access granted — they can now see this project’s namespaces.' });
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not grant access.' });
    } finally { setBusy(''); }
  };

  const doUnbind = async (bindingId) => {
    if (!window.confirm('Revoke this user’s access to the project?')) return;
    setBusy('bind');
    try {
      await clustersAPI.removeProjectBinding(clusterId, bindingId);
      await loadBindings(activeProject);
    } catch (e) {
      setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not revoke access.' });
    } finally { setBusy(''); }
  };

  // "Teams…" — direct namespace grants to groups and users, beside the Project.
  // A namespace can be in only one Project; this is how it reaches several teams.
  const openTeams = (ns) => setTeamsFor(ns);

  if (rancherClusters.length === 0) {
    return (
      <div style={S.page}>
        <h1 style={S.title}>Namespace Access</h1>
        <div style={S.empty}>
          No managed clusters. Projects and namespace RBAC require a managed cluster.
        </div>
      </div>
    );
  }

  // The sidebar's Active Cluster is a type this feature doesn't support — rather
  // than fall back to some other cluster the user didn't pick, say so and point
  // them at the one control that chooses a cluster anywhere in the app.
  if (!clusterId) {
    return (
      <div style={S.page}>
        <h1 style={S.title}>Namespace Access</h1>
        <div style={S.empty}>
          Namespace Access isn't available for "{globalCluster?.name}". Switch your Active Cluster
          (top left) to a managed cluster to view or set its namespace access.
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Namespace Access</h1>
          <div style={S.subtitle}>
            The cluster management platform decides who sees what — this screen drives its Projects. A namespace
            is visible to a non-admin only when it belongs to a Project <em>and</em> that user
            is bound to it. Users see every workload in the namespaces they are granted.
          </div>
        </div>
        <div style={S.toolbar}>
          <button style={S.btn} onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {msg && <div style={S.banner(msg.kind)}>{msg.text}</div>}

      {summary && (
        <div style={S.statRow}>
          <div style={S.stat}>
            <div style={S.statNum}>{summary.total}</div>
            <div style={S.statLabel}>Namespaces</div>
          </div>
          <div style={S.stat}>
            <div style={{ ...S.statNum, color: 'var(--color-success-500)' }}>{summary.assigned}</div>
            <div style={S.statLabel}>Assigned</div>
          </div>
          <div style={S.stat}>
            <div style={{ ...S.statNum, color: summary.unassigned ? 'var(--color-warning-500)' : 'inherit' }}>
              {summary.unassigned}
            </div>
            <div style={S.statLabel}>Invisible to non-admins</div>
          </div>
          {summary.stale > 0 && (
            <div style={S.stat}>
              <div style={{ ...S.statNum, color: 'var(--color-warning-500)' }}>{summary.stale}</div>
              <div style={S.statLabel}>Stale (other cluster)</div>
            </div>
          )}
        </div>
      )}

      {summary?.stale > 0 && (
        <div style={S.banner('warn')}>
          <strong>{summary.stale} namespace{summary.stale === 1 ? '' : 's'} point at another
          cluster's project.</strong> The management platform ignores a cross-cluster assignment, so these look
          configured but behave as unassigned. Re-assign them to a project on this cluster.
        </div>
      )}

      {/* ── Projects ─────────────────────────────────────────────────────── */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Projects</h2>
        <div style={S.cardHint}>
          A Project is the unit permissions are granted on. Group namespaces by who owns them —
          binding someone to a project gives them every namespace inside it.
        </div>
        <div style={S.toolbar}>
          {projects.map(p => (
            <button
              key={p.project_id}
              onClick={() => loadBindings(p.project_id)}
              style={{
                ...S.btn,
                ...(activeProject === p.project_id
                  ? { borderColor: 'var(--brand)', color: 'var(--brand)' } : {}),
              }}
            >
              {p.name}
              <span style={{ color: 'var(--muted)', fontWeight: 400, marginLeft: 6 }}>
                {namespaces.filter(n => n.assigned && String(n.project_id).endsWith(p.project_id)).length}
              </span>
            </button>
          ))}
          {canManage && (
            <button style={S.btnPrimary} onClick={() => setNewProject({ name: '', description: '' })}>
              + New project
            </button>
          )}
        </div>

        {activeProject && (
          <div style={{ borderTop: '1px solid var(--surface-2)', paddingTop: 14, marginTop: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Who can see {projectName(activeProject)}
            </div>
            {canManage && (
              <GroupAccessPanel
                clusterId={clusterId}
                kind="project"
                targetId={activeProject}
                S={S}
                setMsg={setMsg}
                onChanged={() => loadBindings(activeProject)}
              />
            )}
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>USERS</div>
            {bindings.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--muted-2)', marginBottom: 10 }}>
                Nobody is bound yet — these namespaces are admin-only.
              </div>
            ) : (
              <table style={{ ...S.table, marginBottom: 12 }}>
                <thead>
                  <tr>
                    <th style={S.th}>Platform user</th>
                    <th style={S.th}>Role</th>
                    <th style={S.th} />
                  </tr>
                </thead>
                <tbody>
                  {bindings.map(b => (
                    <tr key={b.id}>
                      <td style={S.td}>
                        {users.find(u => health[u.id]?.rancher_user_id === b.rancher_user_id)?.username
                          || b.rancher_user_id}
                        <span style={{ color: 'var(--muted-2)', marginLeft: 8, fontSize: 11 }}>
                          {b.rancher_user_id}
                        </span>
                      </td>
                      <td style={S.td}>{b.role}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>
                        {b.via_group ? (
                          <span style={{ fontSize: 12, color: 'var(--muted)' }}
                            title="Managed by the group grant above — revoke it there, or remove the person from the group">
                            via group {b.via_group}
                          </span>
                        ) : canManage && b.role !== 'project-owner' && (
                          <button style={S.btnDanger} onClick={() => doUnbind(b.id)} disabled={busy === 'bind'}>
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {canManage && (
              <div style={S.toolbar}>
                <select
                  style={S.select}
                  defaultValue=""
                  onChange={e => { doBind(e.target.value); e.target.value = ''; }}
                  disabled={busy === 'bind'}
                >
                  <option value="">Add a user…</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.username}
                      {health[u.id]?.healthy === false ? '  (access broken — re-add to fix)' : ''}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  Cluster access is set up automatically — nothing for the user to do.
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Namespaces ───────────────────────────────────────────────────── */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Namespaces</h2>
        <div style={S.cardHint}>
          Unassigned namespaces are invisible to every non-admin, however their permissions are
          set elsewhere.
        </div>

        <div style={S.toolbar}>
          <input
            style={S.input}
            placeholder="Filter by name…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <select style={S.select} value={showOnly} onChange={e => setShowOnly(e.target.value)}>
            <option value="all">All ({namespaces.length})</option>
            <option value="unassigned">Unassigned ({summary?.unassigned ?? 0})</option>
            <option value="assigned">Assigned ({summary?.assigned ?? 0})</option>
            {summary?.stale > 0 && <option value="stale">Stale ({summary.stale})</option>}
          </select>
          {canManage && selected.size > 0 && (
            <>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>{selected.size} selected →</span>
              <select style={S.select} value={bulkProject} onChange={e => setBulkProject(e.target.value)}>
                <option value="">Choose project…</option>
                {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
              <button
                style={S.btn}
                onClick={doStageBulk}
                disabled={!bulkProject}
                title="Stage this project for the checked namespaces — check a different set and stage again before Applying, to spread namespaces across several projects in one commit."
              >
                {`Stage ${selected.size} → ${projectName(bulkProject) || '…'}`}
              </button>
            </>
          )}
        </div>

        {canManage && Object.keys(pending).length > 0 && (
          <div style={{ ...S.toolbar, marginTop: -6 }}>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              {Object.keys(pending).length} pending change{Object.keys(pending).length === 1 ? '' : 's'}
            </span>
            <button
              style={S.btnPrimary}
              onClick={doApplyPending}
              disabled={busy === 'apply'}
            >
              {busy === 'apply' ? 'Applying…' : `Apply ${Object.keys(pending).length} pending change${Object.keys(pending).length === 1 ? '' : 's'}`}
            </button>
            <button style={S.btn} onClick={doDiscardPending} disabled={busy === 'apply'}>
              Discard
            </button>
          </div>
        )}

        <div style={S.scroll}>
          <table style={S.table}>
            <thead>
              <tr>
                {canManage && (
                  <th style={{ ...S.th, width: 34 }}>
                    <input
                      type="checkbox"
                      checked={visible.length > 0 && visible.every(n => selected.has(n.name))}
                      onChange={toggleAllVisible}
                    />
                  </th>
                )}
                <th style={S.th}>Namespace</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Project</th>
                {canManage && <th style={S.th}>Move to</th>}
                {canManage && <th style={S.th}>Teams</th>}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={canManage ? 6 : 3} style={S.empty}>No namespaces match.</td></tr>
              ) : visible.map(n => (
                <tr key={n.name}>
                  {canManage && (
                    <td style={S.td}>
                      <input type="checkbox" checked={selected.has(n.name)} onChange={() => toggle(n.name)} />
                    </td>
                  )}
                  <td style={{ ...S.td, fontWeight: 500 }}>{n.name}</td>
                  <td style={S.td}>
                    {n.stale ? <span style={PILL.stale}>stale</span>
                      : n.assigned ? <span style={PILL.assigned}>assigned</span>
                      : <span style={PILL.unassigned}>unassigned</span>}
                  </td>
                  <td style={{ ...S.td, color: n.assigned ? 'inherit' : 'var(--muted-2)' }}>
                    {n.assigned ? projectName(n.project_id)
                      : n.stale ? <span title={n.project_id}>{n.project_id}</span>
                      : '—'}
                    {Object.prototype.hasOwnProperty.call(pending, n.name) && (
                      <span style={{ ...S.pill('rgba(37,99,235,0.12)', 'var(--brand)'), marginLeft: 6 }}>
                        → {pending[n.name] ? projectName(pending[n.name]) : 'remove'} (pending)
                      </span>
                    )}
                  </td>
                  {canManage && (
                    <td style={S.td}>
                      <select
                        style={{ ...S.select, fontSize: 12, padding: '4px 8px' }}
                        value={pending[n.name] ?? ''}
                        onChange={e => doStageOne(n.name, e.target.value)}
                      >
                        <option value="">Move…</option>
                        {projects.map(p => (
                          <option key={p.project_id} value={p.project_id}>{p.name}</option>
                        ))}
                        {(n.assigned || n.stale) && <option value="">— remove —</option>}
                      </select>
                    </td>
                  )}
                  {canManage && (
                    <td style={S.td}>
                      <button style={S.btn} onClick={() => openTeams(n.name)}
                        title="Grant this namespace to additional teams, without moving it out of its Project">
                        Teams…
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {newProject && (
        <div style={S.overlay} onClick={() => setNewProject(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>New project</div>
            <label style={S.label}>Name</label>
            <input
              style={{ ...S.input, width: '100%' }}
              autoFocus
              placeholder="e.g. deepcall, billing, platform"
              value={newProject.name}
              onChange={e => setNewProject({ ...newProject, name: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && doCreateProject()}
            />
            <label style={S.label}>Description (optional)</label>
            <input
              style={{ ...S.input, width: '100%' }}
              value={newProject.description}
              onChange={e => setNewProject({ ...newProject, description: e.target.value })}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 22, justifyContent: 'flex-end' }}>
              <button style={S.btn} onClick={() => setNewProject(null)}>Cancel</button>
              <button style={S.btnPrimary} onClick={doCreateProject} disabled={busy === 'project'}>
                {busy === 'project' ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {teamsFor && (
        <NamespaceTeamsModal
          clusterId={clusterId}
          namespace={teamsFor}
          users={users}
          health={health}
          S={S}
          setMsg={setMsg}
          onUserGranted={loadUserHealth}
          onClose={() => setTeamsFor(null)}
        />
      )}

    </div>
  );
}

